import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { MEAL_TABS, MealTab } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useUrlTab } from '@/hooks/use-linked-record';
import { SoonBadge, SoonPanel } from '@/modules/settings';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { weekdayNameOf } from '@/lib/utils/custom-date/custom-date';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_MY_LUNCH_REQUESTS_ORDER } from '../../graphql/queries';
import { createRequestHistoryTableColumns } from '../../components/request-history-table/request-history-table-columns';
import {
  useCancelLunchRequest,
  useCurrentUserId,
  useGetMyLunchRequests,
  useLunchStatus,
  useMyWorkDays,
  useRequestLunch,
} from '../../hooks/use-lunch';
import { getTargetLunchDay, LunchRequestsPagingResult } from '../../types/lunch.types';

const RequestTabSkeleton = () => (
  <div className="flex flex-col gap-3" aria-busy="true">
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-[4.5rem] w-full" />
      ))}
    </div>
    <Skeleton className="h-10 w-full sm:w-28" />
  </div>
);

const RequestTab = ({ date }: { date: string }) => {
  const { t } = useTranslation();
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const weekDay = weekdayNameOf(date) ?? '';

  const isLunchEnabled = useHasPermission(HRM_PERMISSIONS.PEOPLE.LUNCH_ENABLED);
  // LUNCH_CREATE / LUNCH_CANCEL refuse without these; this only hides the buttons.
  const canRequest = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_REQUEST);
  const canCancel = useHasPermission(HRM_PERMISSIONS.MEAL.LUNCH_CANCEL);
  const { data: workDays, isLoading: isWorkDaysLoading } = useMyWorkDays(date);
  const isWorkDay = Boolean(workDays?.includes(weekDay));
  const {
    data: status,
    isLoading: isStatusLoading,
    error: statusError,
  } = useLunchStatus(date, isLunchEnabled && isWorkDay);
  const menus = status?.MenuList ?? [];
  const isOrderingClosed = status && !status.ShowLunchSubmitButton && !status.ShowLunchCancelButton;
  const existingRequestId = status?.ShowLunchCancelButton ? status.FoodRequestId : '';

  const { mutateAsync: requestLunch, isPending: isRequesting } = useRequestLunch();
  const { mutateAsync: cancelLunchRequest, isPending: isCancelling } = useCancelLunchRequest();

  const handleSubmit = async () => {
    if (!selectedMenuId) return;
    try {
      const result = await requestLunch(selectedMenuId);
      if (result.IsSuccessful) setSelectedMenuId('');
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTargetId) return;
    await cancelLunchRequest(cancelTargetId);
    setCancelTargetId(null);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-4 md:pb-4">
          {/* "Lunch for" or "Order success" depends on the status: a placeholder until it's known. */}
          {isWorkDaysLoading || isStatusLoading ? (
            <Skeleton className="h-6 w-56" />
          ) : (
            <CardTitle>
              {existingRequestId ? t('ORDER_SUCCESS') : t('LUNCH_FOR')} {date}
            </CardTitle>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-3 pt-0 md:pt-0">
          {isWorkDaysLoading ? (
            <RequestTabSkeleton />
          ) : !isLunchEnabled ? (
            <p className="text-sm text-muted-foreground">{t('NOT_PERMITTED_FOR_LUNCH')}</p>
          ) : !isWorkDay ? (
            <p className="text-sm text-muted-foreground">{t('NOT_A_WORK_DAY_FOR_LUNCH')}</p>
          ) : isStatusLoading ? (
            <RequestTabSkeleton />
          ) : statusError ? (
            <p className="text-sm text-destructive">
              {t('ERROR_LOADING_DATA')} {statusError.message}
            </p>
          ) : isOrderingClosed ? (
            <p className="text-sm text-muted-foreground">
              {t(status.MessageKey, { ...status.MessageParams })}
            </p>
          ) : existingRequestId ? (
            <div className="flex flex-col gap-3">
              {/* Same card as a menu option, from the request's own menu snapshot. */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-1 break-words rounded-md border-2 border-primary p-3 text-sm">
                  {status?.MenuName && <span className="font-medium">{status.MenuName}</span>}
                  <span className="text-muted-foreground">{status?.RequestedMenu?.join(', ')}</span>
                </div>
              </div>
              {canCancel && (
                <Button
                  className="w-full sm:w-auto sm:self-start"
                  onClick={() => setCancelTargetId(existingRequestId)}
                >
                  {t('CANCEL_REQUEST')}
                </Button>
              )}
            </div>
          ) : menus.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('NO_ACTIVE_MENU')}</p>
          ) : (
            <>
              <RadioGroup
                value={selectedMenuId}
                onValueChange={setSelectedMenuId}
                className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
              >
                {menus.map((menu) => (
                  <label
                    key={menu.ItemId}
                    className="flex h-full cursor-pointer items-start gap-2 rounded-md border-2 p-3 text-sm"
                  >
                    <RadioGroupItem value={menu.ItemId} className="mt-0.5 shrink-0" />
                    <span className="min-w-0 break-words">
                      <span className="font-medium">{menu.Name}</span>
                      <br />
                      <span className="text-muted-foreground">{menu.MenuItems.join(', ')}</span>
                    </span>
                  </label>
                ))}
              </RadioGroup>

              {canRequest && status?.ShowLunchSubmitButton && (
                <Button
                  className="w-full sm:w-auto sm:self-start"
                  onClick={handleSubmit}
                  loading={isRequesting}
                  disabled={isRequesting || !selectedMenuId}
                >
                  {t('SUBMIT')}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        open={Boolean(cancelTargetId)}
        onOpenChange={(open) => !open && setCancelTargetId(null)}
        title={t('CANCEL_LUNCH_REQUEST')}
        description={t('ARE_YOU_SURE_WANT_TO_CANCEL')}
        confirmText={isCancelling ? t('CANCELLING') : t('CANCEL_REQUEST')}
        preventAutoClose={isCancelling}
        onConfirm={handleCancelConfirm}
      />
    </>
  );
};

const HistoryTab = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MY_LUNCH_REQUESTS_ORDER,
  });

  const { data, isLoading, error } = useGetMyLunchRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({ UserId: userId }, columnWhere),
    order,
  });
  const result = (data as { LunchRequests?: LunchRequestsPagingResult })?.LunchRequests;
  const requests = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = useMemo(() => createRequestHistoryTableColumns({ t }), [t]);

  return (
    <AdvanceDataTable
      {...tableProps}
      data={requests}
      columns={columns}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      pagination={{
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        totalCount,
      }}
      isExpandRowContent={false}
      tableClassName="min-w-[28rem]"
      mobileView="cards"
    />
  );
};

/**
 * Order Meal (HR Module Navigation Map; was Request Lunch): tabs Lunch / Dinner (Soon) / History in the
 * URL (`?tab=`; the old `request` tab opens Lunch).
 */
export const RequestLunchPage = () => {
  const { t } = useTranslation();
  // Today (organization time) up to the 11:00 cutoff, otherwise tomorrow.
  const date = useMemo(() => getTargetLunchDay(), []);
  const [tab, setTab] = useUrlTab<MealTab>(
    [MEAL_TABS.LUNCH, MEAL_TABS.DINNER, MEAL_TABS.HISTORY],
    MEAL_TABS.LUNCH,
    { request: MEAL_TABS.LUNCH }
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('ORDER_MEAL')} />

      <Tabs value={tab} onValueChange={(value) => setTab(value as MealTab)}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value={MEAL_TABS.LUNCH} className="flex-1 sm:flex-none">
            {t('LUNCH')}
          </TabsTrigger>
          <TabsTrigger value={MEAL_TABS.DINNER} className="flex-1 gap-1.5 sm:flex-none">
            {t('DINNER')}
            <SoonBadge />
          </TabsTrigger>
          <TabsTrigger value={MEAL_TABS.HISTORY} className="flex-1 sm:flex-none">
            {t('HISTORY')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={MEAL_TABS.LUNCH} className="mt-4">
          <RequestTab date={date} />
        </TabsContent>
        <TabsContent value={MEAL_TABS.DINNER} className="mt-4">
          <SoonPanel name="DINNER" />
        </TabsContent>
        <TabsContent value={MEAL_TABS.HISTORY} className="mt-4">
          <HistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
